// src/hooks/useTickerOwnership.js
// OWNERSHIP-CAPTURE-ARC — new file.
//
// Same shape as useTickerFilingsSummary(ticker, token) — { loading, error } plus
// the actual data — so OwnershipTab.jsx can be a near-mirror of
// TickerFilingsSummary.jsx's structure. I don't have useTickerFilingsSummary.js's
// real file content this session (not uploaded), so this is reconstructed from
// how TickerFilingsSummary.jsx calls it, not copy-verified — the API_URL/fetch
// pattern below matches FilingLockedRow.jsx/FilingRow.jsx's own confirmed
// convention (process.env.REACT_APP_API_URL fallback to localhost) instead.
//
// REVISED this session: surfaces `locked` and `degraded` as distinct booleans,
// matching ownershipQueryService.js's three-state response (locked = guest,
// degraded = Oracle unavailable, hasData:false = genuinely no rows yet) —
// see that file's header for why these are never blurred into one flag.

import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function useTickerOwnership(ticker, token) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setError('');

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/api/ownership/ticker/${encodeURIComponent(ticker)}`, { headers })
      .then(res => res.json())
      .then(json => {
        if (cancelled) return;
        if (!json.success) {
          setError(json.message || 'Ownership data unavailable');
          setData(null);
        } else {
          setData(json);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Ownership data unavailable');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [ticker, token]);

  return {
    hasData: data ? data.hasData : false,
    locked: data ? !!data.locked : false,
    degraded: data ? !!data.degraded : false,
    reason: data ? data.reason : null,
    snapshot: data ? data.snapshot : null,
    transactions: data ? data.transactions : [],
    loading,
    error,
  };
}
